import * as React from 'react';
import { REPUBS } from '@cx-modules/constants';

import Icon from '~/components/icon';
import Link from '~/components/link';
import Modal from '~/components/modal-component/modal-component';
import { ServicesContext } from '~/providers/services-context';
import { type LoaderProviderData } from '~/providers/loader-context';
import { ModalContext } from '~/providers/modal-context';
import { type MyOCData } from '~/types/myoc';
import MyOCActions from '~/services/actions/myoc-actions';
import { comp } from '~/utils/us-utils';

import MyOCController from '../myoc-controller';
import styles from '../style/mini-myoc.module.css';

interface IProps {
  loaderData: LoaderProviderData;
  myocData: MyOCData;
  onOpenCallback?: () => void;
  styles?: string;
  type: 'desktop' | 'burgerTop' | 'pageButton';
}

class MiniMyOC extends React.Component<IProps, any> {
  static contextType = ServicesContext;

  private myOCActions: MyOCActions;

  constructor(props: IProps) {
    super(props);

    this.myOCActions = new MyOCActions(['no-data']);

    this.renderDesktop = this.renderDesktop.bind(this);
  }

  shouldComponentUpdate(nextProps: IProps) {
    return (
      nextProps.type !== this.props.type ||
      nextProps.myocData.isLoggedIn !== this.props.myocData.isLoggedIn);

  }

  private showUserIcon = () => {
    const isPremium = this.props.myocData.myOCUser?.isPremium;
    const icon = isPremium ? 'PremiumUserIcon' : 'User';
    const testId = isPremium ? 'premium-user-icon' : 'user-icon';

    return <Icon icon={icon} data-testid={testId} />;
  };

  /**
   * Renders the mini myoc element in the top bar of the header.
   *
   * @private
   * @returns {JSX.Element} The mini myoc element.
   */
  private renderDesktop(): JSX.Element {
    const context = this.context as React.ContextType<typeof ServicesContext>;
    const { myocData } = this.props;

    // If the user is logged in
    if (myocData?.isLoggedIn) {
      return (
        <div className={styles.myOC} data-testid="mini-myoc">
					<span className={styles.myOCIconWrapper}>
						{this.showUserIcon()}
						<span className={styles.indicator}> &#x25BE;</span>
					</span>
					<div className={styles.myOCDropdown} data-testid="myoc-dropdown">
						<span className={styles.upArrow} />
						<div className={styles.myOCTitle} data-testid="myoc-title">
							{context.labelActions.get('MY_ODDSCHECKER')}
						</div>
						{context.repub === REPUBS.US.name &&
            <a
              href="myoddschecker/profile"
              title={context.labelActions.get('PROFILE')}>

								<div data-testid="myoc-profile" className={styles.myOCRow}>
									{context.labelActions.get('PROFILE')}
								</div>
							</a>
            }
						<a
              href="myoddschecker/settings"
              title={context.labelActions.get('SETTINGS')}>

							<div data-testid="myoc-settings" className={styles.myOCRow}>
								{context.labelActions.get('SETTINGS')}
							</div>
						</a>
						{context.siteConfig?.subscriptions?.enabled &&
            <a
              href="myoddschecker/subscriptions"
              title={context.labelActions.get('SUBSCRIPTIONS')}>

								<div
                data-testid="myoc-subscriptions"
                className={styles.myOCRow}>

									{context.labelActions.get('SUBSCRIPTIONS')}
								</div>
							</a>
            }
						<div
              data-testid="logout-button"
              className={comp(styles.myOCRow, styles.lastRow)}
              onClick={() => this.myOCActions.logout()}>

							{context.labelActions.get('LOG_OUT')}
						</div>
					</div>
				</div>);

    }

    const loginButton =
    <button
      id="loginLabel"
      className={comp(styles.miniOCButtonDesktopAndTablet, styles.login)}>

				{context.labelActions.get('SIGN_IN')}
			</button>;


    return (
      <Modal transparent returnButton buttonComponent={loginButton}>
				{this.renderMyOCController('Login')}
			</Modal>);

  }

  /**
   * Renders the mini myoc element for the top of the burger menu.
   *
   * @private
   * @returns {JSX.Element} The mini myoc element.
   */
  private renderBurgerTop(): JSX.Element {
    const context = this.context as React.ContextType<typeof ServicesContext>;
    const { onOpenCallback, myocData } = this.props;

    if (myocData?.isLoggedIn) {
      return (
        <Link
          to="myoddschecker/profile"
          title={context.labelActions.get('MY_ODDSCHECKER')}
          childrenData
          legacyAjaxRequest>

					<span className={styles.myOCIconWrapper}>{this.showUserIcon()}</span>
				</Link>);

    }

    const loginButton =
    <button
      id="loginLabel"
      className={comp(styles.miniOCButton, styles.login)}>

				{context.labelActions.get('SIGN_IN')}
			</button>;


    return (
      <Modal
        transparent
        returnButton
        buttonComponent={loginButton}
        openCallback={onOpenCallback}>

				{this.renderMyOCController('Login')}
			</Modal>);

  }

  private renderPageButtonItem(): JSX.Element {
    const context = this.context as React.ContextType<typeof ServicesContext>;
    const { onOpenCallback, myocData } = this.props;

    if (myocData?.isLoggedIn) {
      return (
        <Link
          to="myoddschecker/profile"
          title={context.labelActions.get('MY_ODDSCHECKER')}
          childrenData>

					<span className={styles.myOCIconWrapper}>{this.showUserIcon()}</span>
				</Link>);

    }

    const mobileMyOCButton =
    <div className={styles.loginButton}>
				<button>{context.labelActions.get('SIGN_UP')}</button>
			</div>;


    return (
      <Modal
        returnButton
        buttonComponent={mobileMyOCButton}
        openCallback={onOpenCallback}>

				{this.renderMyOCController('Signup')}
			</Modal>);

  }

  private renderMyOCController(viewToOpen: 'Signup' | 'Login' | 'Profile') {
    const { myocData, loaderData } = this.props;

    return (
      <ModalContext.Consumer>
				{(modalData) =>
        <MyOCController
          viewToOpen={viewToOpen}
          loaderData={loaderData}
          myocData={myocData}
          modalContext={modalData} />

        }
			</ModalContext.Consumer>);

  }

  public render(): JSX.Element | null {
    switch (this.props.type) {
      case 'desktop':
        return this.renderDesktop();
      case 'burgerTop':
        return this.renderBurgerTop();
      case 'pageButton':
        return this.renderPageButtonItem();
      default:
        return null;
    }
  }
}

export default MiniMyOC;